import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import { Set } from 'immutable';
import { includes } from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';

import type { State } from 'types';

const {
    actions: { changeBetslipOddStake, changeBetslipBetStake },
    selectors: { selectBetslipBet, selectBetslipOdds },
    constants: { BETSLIP_ODD_STATUSES_TO_PLACE_BET },
} = betslip;

interface Input {
    betslipBetId: string;
}

type Output = (stake: string) => void;

export default function useChangeStake({ betslipBetId }: Input): Output {
    const dispatch = useDispatch();
    const props = useSelector((s: State) => selectProps(s, { betslipBetId }));

    const propsRef = useRef(props);
    propsRef.current = props;

    const changeStake = useCallback(
        (stake: string) => {
            const { betslipBet, betslipOddIdsToExclude } = propsRef.current;

            if (!betslipBet) return;

            const { betslipOddIds, id } = betslipBet;

            betslipOddIds
                .filterNot(betslipOddId =>
                    betslipOddIdsToExclude.has(betslipOddId)
                )
                .forEach(betslipOddId => {
                    dispatch(changeBetslipOddStake({ betslipOddId, stake }));
                });

            dispatch(changeBetslipBetStake({ betslipBetId: id, stake }));
        },
        [dispatch]
    );

    return changeStake;
}

const selectBaseBetslipOddIdsToExclude = createSelector(
    selectBetslipBet,
    selectBetslipOdds,
    (betslipBet, betslipOdds) => {
        if (!betslipBet) return Set<string>();

        return betslipBet.betslipOddIds.filter(betslipOddId => {
            const betslipOdd = betslipOdds.get(betslipOddId);

            if (!betslipOdd) return true;

            const { singleStatus } = betslipOdd;

            return !includes(BETSLIP_ODD_STATUSES_TO_PLACE_BET, singleStatus);
        });
    }
);

const selectProps = createStructuredSelector({
    betslipBet: selectBetslipBet,
    betslipOddIdsToExclude: selectBaseBetslipOddIdsToExclude,
});
